// Lane D (10/15/26): the pure parts of Saved and the group redesign, kept free of React and the network so
// tests/groups_lane_d_ui_test.ts proves them offline.
import { calendarLocation } from './calLocation.ts'
import { buildCalendarInvite } from './calendarInvite.ts'
import { matchesKey } from './categories.ts'
import { dayFit, lateToday, wallTime, type DayMiss, type FitItem } from './fit.ts'

/** B-148: the count Saved shows. The Saved list, plus saves this tab sent that the next load will show, minus unsaves.
 *  Null before the first load. Never the Browse store's "added" flags (dated itineraries, groups, a cached page). */
export function savedCountOf(entries: { itemId: string }[] | null, removed: Set<string>, pending: Map<string, number> | Set<string>): number | null {
  if (!entries) return null
  const ids = new Set(entries.map((e) => e.itemId))
  for (const id of pending.keys()) ids.add(id)
  for (const id of removed) ids.delete(id)
  return ids.size
}

export type DayPick = { item: string; start?: string; approx?: boolean }
/**
 * Pin 578: the owner checks the group's activities and puts them in order; this turns that into the day's picks. A thing
 * with a time of its own (an event, a time in its text; not a guessed part of day) keeps it (`known`, the itinerary's own pickFor);
 * the rest follow in the owner's order from the start time, two hours apart. The server still keeps every stop inside
 * opening hours (and the day's own rules), so this is a request, not a promise.
 */
export function orderedPicks<T extends { id: string }>(items: T[], ymd: string, startHm: string, tz: string, known: (it: T) => DayPick): DayPick[] {
  const [h, m] = startHm.split(':').map(Number)
  let at = (Number.isFinite(h) ? h : 14) + (Number.isFinite(m) ? m : 0) / 60
  return items.map((it) => {
    const k = known(it)
    // only a real time of its own (an event's start, a time in its own words: approx false) beats the owner's order; a
    // guessed part-of-day hour does not (blind round 2, Rosa: a museum's 10 AM guess overrode her 2 PM start and order)
    if (k.start && k.approx !== true) return k
    const start = wallTime(ymd, at, tz)
    at = Math.min(at + 2, 23)
    return { item: it.id, start }
  })
}

/**
 * Prod data check 10/16 (lock fallback): a stop the server refused at the asked time is only offered again, at a time the
 * server picks, when the day itself works for it, by the same rules the day builder greys things out with (dayFit: open
 * that weekday, running that day, its own dates; plus too late today). Otherwise it is left out with this reason, never put
 * on a day it is closed (a museum closed Tuesdays went on at 10 AM over a hike). Null = it may go on.
 */
export function lockFallbackMiss(item: FitItem, ymd: string, tz: string | null, now = new Date()): DayMiss | { code: 'too_late' } | null {
  const d = dayFit({ item, tz }, ymd, now)
  if (d) return d
  return lateToday(item as never, ymd, tz, now) ? { code: 'too_late' } : null
}
/** The server's answer to the fallback add: a stop on top of another one, or one it could not time inside its known
 *  opening hours ("check hours" with hours we do know), does not stay on the day. */
export function fallbackKeeps(r: { conflict?: boolean; check_hours?: boolean } | null | undefined, hoursKnown: boolean): boolean {
  return !(r?.conflict === true || (r?.check_hours === true && hoursKnown))
}

/** Move one entry of a list up (-1) or down (+1); out of range leaves it as it is. */
export function moveIn<T>(list: T[], i: number, by: -1 | 1): T[] {
  const j = i + by
  if (i < 0 || i >= list.length || j < 0 || j >= list.length) return list
  const out = [...list]
  ;[out[i], out[j]] = [out[j], out[i]]
  return out
}

export type PickSource = 'picks' | 'saved'
export type GroupPickRow<T> = { item: T; source: PickSource; everyone: boolean | null }
/** Pin 582: the group's picks and the person's own saved things in one list, narrowed by where it came from, a Browse
 *  category and "fits everyone" (a saved thing nobody has judged yet does not count as fitting). */
export function filterPickRows<T extends { id: string; category: string; tags?: string[] | null; title: string; one_line?: string | null }>(
  rows: GroupPickRow<T>[], f: { source: PickSource | 'all'; cat: string | null; everyone: boolean },
): GroupPickRow<T>[] {
  return rows.filter((r) => (f.source === 'all' || r.source === f.source) && (!f.cat || matchesKey(r.item, f.cat)) && (!f.everyone || r.everyone === true))
}

// ---------- the calendar file of a locked group plan: one event per activity, the guests the group gave ----------
type IcsItem = { id: string; title: string; url?: string | null; venue_name?: string | null; address?: string | null; neighborhood?: string | null }
export type IcsStop = { item: IcsItem; start_at: string; end_at: string | null }

/**
 * Pin 589 / B-150: the locked plan as ONE calendar file with one event per activity at its itinerary time (never one block
 * for the day), and every email the group gave on each event's guest list. METHOD:PUBLISH: importing it adds the events;
 * Magellan sends nothing, and the person's own calendar app asks before it invites anyone.
 */
export function groupIcs(stops: IcsStop[], o: { group: string; emails: string[]; uid: string; now?: Date; line?: string }): string {
  // pin 596: the one calendar-invite door (lib/calendarInvite.ts); same events, guests and UIDs as before
  return buildCalendarInvite(stops.map((s, i) => {
    const start = new Date(s.start_at)
    const end = s.end_at && Date.parse(s.end_at) > start.getTime() ? s.end_at : new Date(start.getTime() + 90 * 60e3).toISOString()
    return { title: s.item.title, start: s.start_at, end, location: calendarLocation(s.item) || null, url: null, note: [o.line ?? o.group, s.item.url ? `Details: ${s.item.url}` : ''].filter(Boolean).join('\n'), uid: `${o.uid}-${i}` }
  }), { guests: o.emails, now: o.now }).ics
}

/** B-156: the name a signed-in person already gave us (the same rule as the group function's logic.ts knownName): their
 *  profile name, else their Google first name, else the first word of their full name. Null for a visitor. */
export function knownName(meta: Record<string, unknown> | null | undefined, profileName?: string | null): string | null {
  // eslint-disable-next-line no-control-regex
  const clean = (s: unknown) => String(s ?? '').replace(/[<>\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40)
  const p = clean(profileName)
  if (p) return p
  if (!meta) return null
  const given = clean(meta.given_name)
  if (given) return given
  const full = clean(meta.full_name ?? meta.name)
  return full ? full.split(' ')[0] : null
}
